/**
 * S5 chart #1 — the centerpiece. A markets-terminal trend of how prominent a
 * tracked term is over time, per agenda, with Gary's uploads AND exogenous
 * events (Budgets, elections, reports) marked on the timeline, and detected
 * spikes flagged.
 *
 * Discipline (plan §1.1–§1.2): the three AGENDAS (public / media / elite) are
 * NEVER blended onto one number — you switch between them with the tabs.
 *
 * Within an agenda, what shares one axis differs:
 *   • media / elite — one line per GEO (worldwide vs UK vs US), a comparable
 *     scale, so the UK-vs-global story reads off one chart. Media headlines on
 *     SHARE (% of coverage), elite on item VOLUME (no agenda denominator yet).
 *   • public — the TWO public sub-signals (Google Search interest + YouTube
 *     discussion volume) on one chart, each INDEXED to its own recent normal
 *     (1× = business-as-usual). They're not commensurable in raw units (a 0–100
 *     Trends index vs a within-sample %), so indexing is the only honest co-plot:
 *     the lines show relative MOVEMENT, letting you see whether search and
 *     discourse rise together or diverge. Raw levels live in the tiles above.
 *     Search was previously its own tab; folding it under Public restores the
 *     academic construct (plan §1.2: Public = search + YouTube).
 *
 * Uploads turn the monitor into an attribution tool; the exogenous markers keep
 * that attribution honest — a spike on Budget day isn't cut-through.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  SalienceAgenda,
  SalienceArticle,
  SalienceCalendarEvent,
  SalienceMetricPoint,
  SalienceObservation,
  SalienceSpike,
  Video,
} from "../../lib/api";
import { apiSalience, SALIENCE_AGENDA_BLURB } from "../../lib/api";
import { ArticleList, ArticleListSkeleton } from "./ArticleList";
import { EliteItemList } from "./EliteItemList";
import {
  YouTubeVideoList,
  YouTubeVideoListSkeleton,
} from "./YouTubeVideoList";
import { Segmented } from "../charts/Segmented";
import { WindowNav } from "../charts/WindowNav";
import { useTimeWindow } from "../charts/useTimeWindow";
import { useGeoToggle } from "../charts/useGeoToggle";
import { SmoothToggle } from "../charts/SmoothToggle";
import {
  agendaMetric,
  bucketTs,
  EVENT_TYPE_COLOURS,
  formatAgendaValue,
  GEO_COLOURS,
  GEO_LABELS,
  geosForAgenda,
  makeTimeTickFormatter,
  metricValue,
  rollingMean,
  shortDate,
  windowDate,
  zeroFillDaily,
} from "./format";

const SMOOTH_WINDOW_DAYS = 7;

const UPLOAD_COLOUR = "#aab2ff";

/** The public agenda's two sub-signals, co-plotted (indexed) on one chart. Each
 * is further split PER GEO where the source exposes one — Search has a real
 * worldwide/UK/US split (Google Trends); YouTube topic volume is worldwide-only.
 * So a combined series is keyed `${signal}__${geo}` and every line toggles
 * independently, same as the per-geo lines on the Media/Elite tabs. */
const PUBLIC_SIGNALS = ["search", "public"] as const;
const SIGNAL_LABELS: Record<string, string> = {
  search: "Search",
  public: "YouTube",
};
/** Search reuses the app-wide geo palette (worldwide=blue / UK=green / US=amber)
 * so a region reads the same colour it does on every other chart; YouTube gets
 * its own hue (it only has worldwide, so there's no geo collision to resolve). */
const YT_GEO_COLOURS: Record<string, string> = {
  worldwide: "#c084fc", // purple
  GB: "#e879a6",
  UK: "#e879a6",
};
/** Split a combined series key into [signal, geo]. */
function splitSeriesKey(key: string): [string, string] {
  const i = key.indexOf("__");
  return i === -1 ? [key, ""] : [key.slice(0, i), key.slice(i + 2)];
}
const COMBINED_PUBLIC_BLURB =
  "Public attention — Google search interest (by region) and YouTube discussion " +
  "volume, each line indexed to its own recent normal (1× = business as usual). " +
  "The lines show relative movement, not absolute level — raw figures are in the " +
  "tiles above. Tap a label below to hide or show any region.";

/** A small upward-triangle flag sitting on the x-axis, marking a Gary upload. */
function renderUploadFlag(props: { cx?: number; cy?: number }) {
  const { cx, cy } = props;
  if (cx == null || cy == null) return <g />;
  return (
    <path
      d={`M${cx},${cy - 9} L${cx - 4},${cy} L${cx + 4},${cy} Z`}
      fill={UPLOAD_COLOUR}
      opacity={0.95}
    />
  );
}

type RangeKey = "30d" | "90d" | "1y" | "all";
const RANGES: { key: RangeKey; label: string; days: number | null }[] = [
  { key: "30d", label: "30D", days: 30 },
  { key: "90d", label: "90D", days: 90 },
  { key: "1y", label: "1Y", days: 365 },
  { key: "all", label: "All", days: null },
];

// Search is no longer a top-level tab — it's folded into the combined Public
// view. Tabs are now the three academic agendas.
const AGENDA_TABS: SalienceAgenda[] = ["media", "public", "elite"];
const AGENDA_TAB_LABELS: Record<string, string> = {
  media: "Media",
  public: "Public",
  elite: "Elite",
};

interface ShareTrendChartProps {
  termId: string;
  termLabel: string;
  points: SalienceMetricPoint[];
  spikes: SalienceSpike[];
  calendar: SalienceCalendarEvent[];
  uploads: Video[];
  defaultAgenda?: SalienceAgenda;
}

export function ShareTrendChart({
  termId,
  termLabel,
  points,
  spikes,
  calendar,
  uploads,
  defaultAgenda = "media",
}: ShareTrendChartProps) {
  // Which agenda tabs actually have data for this term — don't offer empty
  // tabs. The Public tab counts if EITHER sub-signal (search or youtube) has
  // data, since it now co-plots both.
  const agendasWithData = useMemo(() => {
    const present = new Set(points.map((p) => p.agenda));
    return AGENDA_TABS.filter((a) =>
      a === "public"
        ? present.has("public") || present.has("search")
        : present.has(a),
    );
  }, [points]);

  const [agenda, setAgenda] = useState<SalienceAgenda>(
    agendasWithData.includes(defaultAgenda)
      ? defaultAgenda
      : agendasWithData[0] ?? defaultAgenda,
  );
  const [showUploads, setShowUploads] = useState(true);
  // Opt-in 7-day moving average — tames the spiky raw daily share into a trend
  // line (default off; raw is the ground truth). See rollingMean.
  const [smooth, setSmooth] = useState(false);
  // Click-a-day drilldown (media + public + elite agendas): a plain click — a
  // "drag" spanning under 2 days — selects that day and loads its provenance on
  // demand (media → articles, public → YouTube videos, elite → gov items).
  const [coverageDay, setCoverageDay] = useState<string | null>(null);

  const activeAgenda = agendasWithData.includes(agenda)
    ? agenda
    : agendasWithData[0] ?? agenda;

  // The Public tab is the combined (search + youtube) indexed view; every other
  // agenda keeps the per-geo path.
  const combined = activeAgenda === "public";

  // Geos each public sub-signal actually exposes (Search: worldwide/UK/US;
  // YouTube: worldwide-only). Only relevant when combined.
  const signalGeos = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const s of PUBLIC_SIGNALS) {
      const present = new Set(
        points
          .filter((p) => p.agenda === s && metricValue(p, s) != null)
          .map((p) => p.geo),
      );
      const ordered = geosForAgenda(s).filter((g) => present.has(g));
      if (ordered.length) out[s] = ordered;
    }
    return out;
  }, [points]);

  // Per-geo series for the non-combined agendas.
  const geos = useMemo(() => {
    if (combined) return [];
    const present = new Set(
      points.filter((p) => p.agenda === activeAgenda).map((p) => p.geo),
    );
    return geosForAgenda(activeAgenda).filter((g) => present.has(g));
  }, [points, activeAgenda, combined]);

  // The keys rendered as lines: bare geos for media/elite; `${signal}__${geo}`
  // composite keys for the combined Public view (one line per region per signal).
  const seriesKeys = useMemo<string[]>(
    () =>
      combined
        ? PUBLIC_SIGNALS.flatMap((s) =>
            (signalGeos[s] ?? []).map((g) => `${s}__${g}`),
          )
        : geos,
    [combined, geos, signalGeos],
  );

  const colourFor = (k: string): string => {
    if (!combined) return GEO_COLOURS[k] ?? "#4f8cff";
    const [sig, geo] = splitSeriesKey(k);
    return sig === "public"
      ? YT_GEO_COLOURS[geo] ?? "#c084fc"
      : GEO_COLOURS[geo] ?? "#4f8cff";
  };
  const labelFor = (k: string): string => {
    if (!combined) return GEO_LABELS[k] ?? k;
    const [sig, geo] = splitSeriesKey(k);
    return `${SIGNAL_LABELS[sig] ?? sig} · ${GEO_LABELS[geo] ?? geo}`;
  };

  // Per-series visibility — click a legend chip to hide/show that line; the Y
  // axis rescales to whatever stays on (see useGeoToggle — generic over any key
  // set, geo or signal×geo). Resets to all-visible when the key set changes.
  const seriesToggle = useGeoToggle(seriesKeys);
  const { visibleGeos: visibleKeys } = seriesToggle;

  // Full render series — windowing slices this client-side, so scrolling and
  // zooming never refetch. For combined, EACH signal×geo line is INDEXED to its
  // own mean (baseline), so non-commensurable units (a 0–100 Trends index vs a
  // within-sample count) become one relative-movement axis and regions stay
  // comparable to each other.
  // Zero-fill boundary for count-per-day series: the term's latest bucket on
  // ANY agenda, capped at today. A missing elite day inside the collected span
  // means "no relevant items" (the daily gov collectors + classifier keep it
  // ≤1 day behind), so it must plot as 0, not be interpolated over — but if the
  // whole pipeline stalls, this boundary stops advancing and we don't invent
  // zeros beyond the last real data.
  const volumeFillEndTs = useMemo(() => {
    if (!points.length) return 0;
    let latest = 0;
    for (const p of points) {
      const ts = bucketTs(p.bucketDate);
      if (ts > latest) latest = ts;
    }
    const todayTs = bucketTs(new Date().toISOString().slice(0, 10));
    return Math.min(latest, todayTs);
  }, [points]);

  const { allRows, combinedBaselines } = useMemo(() => {
    if (!combined) {
      const agendaPoints = points.filter((p) => p.agenda === activeAgenda);
      const byTs = new Map<number, Record<string, number>>();
      for (const p of agendaPoints) {
        const ts = bucketTs(p.bucketDate);
        const row = byTs.get(ts) ?? { ts };
        const v = metricValue(p, activeAgenda);
        if (v != null) row[p.geo] = v;
        byTs.set(ts, row);
      }
      const rows = [...byTs.values()].sort((a, b) => a.ts - b.ts);
      return {
        allRows:
          agendaMetric(activeAgenda) === "volume"
            ? zeroFillDaily(rows, geos, volumeFillEndTs)
            : rows,
        combinedBaselines: {} as Record<string, number>,
      };
    }

    // Raw rows: one column per signal×geo series (`${signal}__${geo}`).
    const rawByTs = new Map<number, Record<string, number>>();
    for (const s of PUBLIC_SIGNALS) {
      for (const g of signalGeos[s] ?? []) {
        const key = `${s}__${g}`;
        for (const p of points) {
          if (p.agenda !== s || p.geo !== g) continue;
          const v = metricValue(p, s);
          if (v == null) continue;
          const ts = bucketTs(p.bucketDate);
          const row = rawByTs.get(ts) ?? { ts };
          row[key] = v;
          rawByTs.set(ts, row);
        }
      }
    }

    // YouTube discussion volume is a count-per-day series like elite — a bucket
    // only exists on days with ≥1 relevant video, so interpolation manufactures
    // plateaus. Zero-fill its interior gaps only (no fillEnd): the collector is
    // weekly, so days after its last bucket are "not yet collected", not zero.
    // Search (weekly Trends index) is NOT filled — missing ≠ zero there.
    const ytKeys = (signalGeos.public ?? []).map((g) => `public__${g}`);
    const rawRows = zeroFillDaily([...rawByTs.values()], ytKeys);

    // Baseline = mean of each series' non-null values across the full window
    // (zero-filled quiet days included for YouTube — that IS its normal).
    const keys = seriesKeys;
    const baselines: Record<string, number> = {};
    for (const key of keys) {
      let sum = 0;
      let n = 0;
      for (const row of rawRows)
        if (row[key] != null) {
          sum += row[key];
          n += 1;
        }
      if (n > 0) baselines[key] = sum / n;
    }

    // Index every value by its series' baseline → "× its own normal".
    const idxRows = rawRows
      .map((row) => {
        const out: Record<string, number> = { ts: row.ts };
        for (const key of keys)
          if (row[key] != null && baselines[key])
            out[key] = row[key] / baselines[key];
        return out;
      })
      .sort((a, b) => a.ts - b.ts);

    return { allRows: idxRows, combinedBaselines: baselines };
  }, [points, activeAgenda, combined, geos, volumeFillEndTs, signalGeos, seriesKeys]);

  const dataMinTs = allRows.length ? allRows[0].ts : 0;
  const dataMaxTs = allRows.length ? allRows[allRows.length - 1].ts : 0;

  // Shared window state — range presets + drag-zoom + ◀▶ nav (see useTimeWindow).
  const tw = useTimeWindow({
    ranges: RANGES,
    defaultRange: "90d",
    dataMinTs,
    dataMaxTs,
  });
  const { winLo, winHi } = tw;

  // Reset the window (and the day drilldown) when the term changes — otherwise a
  // new term would render inside the previous term's zoom. This component is
  // reused across term pages, so termId really does change under it.
  useEffect(() => {
    setCoverageDay(null);
    tw.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId]);

  // Smooth on the FULL series (not the windowed slice) so the leftmost visible
  // points still get their trailing-window context from data just off-screen.
  const renderRows = useMemo(
    () => (smooth ? rollingMean(allRows, seriesKeys, SMOOTH_WINDOW_DAYS) : allRows),
    [smooth, allRows, seriesKeys],
  );

  const { data, domainMin, domainMax } = useMemo(() => {
    const rows = renderRows.filter((r) => r.ts >= winLo && r.ts <= winHi);
    return {
      data: rows,
      domainMin: rows.length ? rows[0].ts : 0,
      domainMax: rows.length ? rows[rows.length - 1].ts : 0,
    };
  }, [renderRows, winLo, winHi]);

  // ts → rendered row, so spike dots can ride the smoothed line (a raw-valued
  // dot would float off a smoothed line and, being above the rescaled domain,
  // get clipped by recharts' default discard-on-overflow).
  const renderByTs = useMemo(() => {
    const m = new Map<number, Record<string, number>>();
    for (const r of renderRows) m.set(r.ts, r);
    return m;
  }, [renderRows]);

  // Commit a drag: the hook turns it into a zoom or reports a sub-2-day CLICK,
  // which on the media/public agendas opens that day's provenance drilldown
  // (public always drills the YouTube sub-signal — Search has no per-day source).
  function handleDragEnd() {
    const r = tw.onDragEnd();
    if (
      r?.kind === "click" &&
      (activeAgenda === "media" || activeAgenda === "elite" || combined)
    ) {
      const day = new Date(r.ts).toISOString().slice(0, 10);
      setCoverageDay((cur) => (cur === day ? null : day));
    }
  }

  // Markers within the visible x-domain.
  const uploadMarkers = useMemo(() => {
    if (!showUploads) return [];
    return uploads
      .map((v) => ({ ts: bucketTs(v.publishedAt.slice(0, 10)), title: v.title }))
      .filter((u) => u.ts >= domainMin && u.ts <= domainMax)
      .sort((a, b) => a.ts - b.ts);
  }, [uploads, showUploads, domainMin, domainMax]);

  const eventMarkers = useMemo(
    () =>
      calendar
        .map((e) => ({ ...e, ts: bucketTs(e.eventDate) }))
        .filter((e) => e.ts >= domainMin && e.ts <= domainMax),
    [calendar, domainMin, domainMax],
  );

  const spikeDots = useMemo(() => {
    // Each spike sits on the line it belongs to: `${agenda}__${geo}` when
    // combined, bare geo otherwise. Only render it if that line is visible.
    const src = combined
      ? spikes
          .map((s) => ({ s, key: `${s.agenda}__${s.geo}` }))
          .filter(({ key }) => visibleKeys.includes(key))
      : spikes
          .filter((s) => s.agenda === activeAgenda && visibleKeys.includes(s.geo))
          .map((s) => ({ s, key: s.geo }));
    return src
      .map(({ s, key }) => {
        const ts = bucketTs(s.bucketDate);
        let value: number;
        if (combined) {
          const base = combinedBaselines[key];
          const idx = base ? (s.value ?? 0) / base : 0;
          value = smooth ? renderByTs.get(ts)?.[key] ?? idx : idx;
        } else {
          const raw = s.value ?? 0;
          value = smooth ? renderByTs.get(ts)?.[key] ?? raw : raw;
        }
        return { ts, value, nearGary: s.nearGaryUpload };
      })
      .filter((s) => s.ts >= domainMin && s.ts <= domainMax);
  }, [
    spikes,
    combined,
    activeAgenda,
    visibleKeys,
    combinedBaselines,
    smooth,
    renderByTs,
    domainMin,
    domainMax,
  ]);

  const fmtY = (v: number) =>
    combined ? `${v.toFixed(1)}×` : formatAgendaValue(v, activeAgenda);
  const tipVal = (_k: string, v: number) =>
    combined ? `${v.toFixed(2)}×` : formatAgendaValue(v, activeAgenda);

  const agendaName = combined ? "Public" : AGENDA_TAB_LABELS[activeAgenda];
  const blurb = combined ? COMBINED_PUBLIC_BLURB : SALIENCE_AGENDA_BLURB[activeAgenda];

  return (
    <section className="rounded-xl border border-white/10 bg-terminal p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
            Attention over time
          </div>
          <h2 className="mt-0.5 text-lg font-bold tracking-tight text-terminal-ink">
            {termLabel}
          </h2>
        </div>
        <div className="flex flex-col items-end gap-2">
          {agendasWithData.length > 1 && (
            <Segmented
              options={agendasWithData.map((a) => ({
                key: a,
                label: AGENDA_TAB_LABELS[a],
              }))}
              value={activeAgenda}
              onChange={setAgenda}
            />
          )}
          <Segmented
            options={RANGES}
            value={tw.range}
            onChange={tw.setRange}
            size="sm"
          />
          {tw.showNav && allRows.length > 0 && (
            <WindowNav
              canBack={tw.canBack}
              canFwd={tw.canFwd}
              onBack={() => tw.shift(-1)}
              onFwd={() => tw.shift(1)}
              label={`${windowDate(domainMin || winLo)} – ${windowDate(domainMax || winHi)}`}
              isWindowed={tw.isWindowed}
              onReset={tw.reset}
              resetLabel={tw.isCustom ? "Reset zoom" : "Latest"}
            />
          )}
        </div>
      </div>

      <div className="mb-2 text-[11px] text-terminal-ink-muted">
        {blurb}
        {data.length > 1 && (
          <span className="opacity-70">
            {" "}· Drag across the chart to zoom
            {activeAgenda === "media" && "; click a day to see its coverage"}
            {activeAgenda === "elite" && "; click a day to see its activity"}
            {combined && "; click a day to see its videos"}.
          </span>
        )}
      </div>

      {data.length === 0 ? (
        <div className="flex h-[300px] flex-col items-center justify-center gap-2 rounded-lg border border-white/5 bg-terminal-panel/40 text-sm text-terminal-ink-muted">
          <span>
            No {agendaName.toLowerCase()} series for {termLabel} in this window.
          </span>
          {tw.isWindowed && (
            <button
              type="button"
              onClick={tw.reset}
              className="rounded border border-white/10 bg-terminal-panel px-2 py-1 text-xs font-semibold transition-colors hover:text-terminal-ink"
            >
              Back to latest
            </button>
          )}
        </div>
      ) : (
        <div className="relative h-[320px] w-full select-none">
          {seriesToggle.allHidden && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm text-terminal-ink-muted">
              All lines hidden — tap a label below to show one.
            </div>
          )}
          <div className="absolute inset-0">
          <ResponsiveContainer>
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 10, bottom: 0, left: 0 }}
              onMouseDown={(e) => {
                const ts = Number(e?.activeLabel);
                if (Number.isFinite(ts)) tw.onDragStart(ts);
              }}
              onMouseMove={(e) => {
                const ts = Number(e?.activeLabel);
                if (Number.isFinite(ts)) tw.onDragMove(ts);
              }}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
            >
              <defs>
                {/* Only the (non-combined) worldwide geo line gets an area fill;
                    the two combined sub-signal lines are plain lines so neither
                    fill paints over the other. */}
                {!combined &&
                  geos.map((g) => (
                    <linearGradient
                      key={g}
                      id={`grad-${g}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={GEO_COLOURS[g]}
                        stopOpacity={0.28}
                      />
                      <stop
                        offset="100%"
                        stopColor={GEO_COLOURS[g]}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  ))}
              </defs>
              <CartesianGrid
                stroke="rgba(255,255,255,0.07)"
                vertical={false}
              />
              <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tickFormatter={makeTimeTickFormatter(domainMin, domainMax)}
                stroke="#5a5f9e"
                tick={{ fontSize: 10, fill: "#9aa0d8" }}
                tickLine={false}
                minTickGap={36}
              />
              <YAxis
                tickFormatter={fmtY}
                stroke="#5a5f9e"
                tick={{ fontSize: 10, fill: "#9aa0d8" }}
                tickLine={false}
                axisLine={false}
                width={44}
              />

              {/* Reference line at 1× (business-as-usual) for the indexed view. */}
              {combined && (
                <ReferenceLine
                  y={1}
                  stroke="rgba(255,255,255,0.18)"
                  strokeDasharray="2 3"
                />
              )}

              {visibleKeys.map((k) =>
                !combined && k === "worldwide" ? (
                  <Area
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={colourFor(k)}
                    strokeWidth={2}
                    fill={`url(#grad-${k})`}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ) : (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    stroke={colourFor(k)}
                    strokeWidth={combined ? 2 : 1.75}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ),
              )}

              {/* Markers drawn AFTER the series so the area fill doesn't paint
                  over them. Gary uploads = the attribution overlay, rendered as
                  flag markers pinned to the x-axis (faint full-height lines
                  vanished against the dark fill); exogenous events = the
                  confounder guard. */}
              {uploadMarkers.map((u, i) => (
                <ReferenceDot
                  key={`up-${i}`}
                  x={u.ts}
                  y={0}
                  r={0}
                  isFront
                  shape={renderUploadFlag}
                />
              ))}

              {/* Exogenous events — labelled dashed lines, bolder than uploads. */}
              {eventMarkers.map((e, i) => (
                <ReferenceLine
                  key={`ev-${i}`}
                  x={e.ts}
                  stroke={EVENT_TYPE_COLOURS[e.eventType] ?? "#9aa0d8"}
                  strokeDasharray="4 3"
                  strokeWidth={1.25}
                  strokeOpacity={0.9}
                  label={{
                    value: e.label,
                    position: "insideTopRight",
                    fill: EVENT_TYPE_COLOURS[e.eventType] ?? "#9aa0d8",
                    fontSize: 9,
                  }}
                />
              ))}

              {/* Drag-to-zoom selection highlight. */}
              {tw.dragStart != null &&
                tw.dragCur != null &&
                Math.abs(tw.dragCur - tw.dragStart) > 0 && (
                  <ReferenceArea
                    x1={Math.min(tw.dragStart, tw.dragCur)}
                    x2={Math.max(tw.dragStart, tw.dragCur)}
                    fill="#aab2ff"
                    fillOpacity={0.12}
                    stroke="#aab2ff"
                    strokeOpacity={0.35}
                  />
                )}

              {/* Spike flags. */}
              {spikeDots.map((s, i) => (
                <ReferenceDot
                  key={`sp-${i}`}
                  x={s.ts}
                  y={s.value}
                  r={4}
                  fill={s.nearGary ? "#16c784" : "#ea3943"}
                  stroke="#0f1036"
                  strokeWidth={1.5}
                />
              ))}

              <Tooltip
                content={
                  <TrendTooltip
                    labelFor={labelFor}
                    fmtVal={tipVal}
                    uploads={uploadMarkers}
                    events={eventMarkers}
                  />
                }
              />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        </div>
      )}

      {(activeAgenda === "media" || activeAgenda === "elite" || combined) &&
        coverageDay && (
          <DayCoverage
            termId={termId}
            termLabel={termLabel}
            agenda={combined ? "public" : activeAgenda}
            day={coverageDay}
            onClose={() => setCoverageDay(null)}
          />
        )}

      <Legend
        seriesKeys={seriesKeys}
        colourFor={colourFor}
        labelFor={labelFor}
        isVisible={seriesToggle.isVisible}
        onToggle={seriesToggle.toggle}
        events={eventMarkers}
        showUploads={showUploads}
        onToggleUploads={() => setShowUploads((v) => !v)}
        smooth={smooth}
        onToggleSmooth={() => setSmooth((v) => !v)}
        hasSpikes={spikeDots.length > 0}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/** The five gov sources behind the elite volume line — mirrors ukgov.SOURCES. */
const ELITE_SOURCES = "hansard,edm,wpq,petition,govuk";

/** On-demand provenance for a clicked chart day.
 *
 * Media: already-captured days come back instantly from salienceArticles; a
 * first look at an uncaptured day runs a one-day BigQuery GKG capture (~seconds).
 * Public + elite: the items are already in salienceObservations (they ARE the
 * volume count), so it's a plain table read — relevant-coded videos with a
 * top-channels summary (Gary's uploads badged), or gov items with a per-source
 * tally and member names. */
function DayCoverage({
  termId,
  termLabel,
  agenda,
  day,
  onClose,
}: {
  termId: string;
  termLabel: string;
  agenda: SalienceAgenda;
  day: string;
  onClose: () => void;
}) {
  const isMedia = agenda === "media";
  const isElite = agenda === "elite";
  const { data, isLoading, error } = useQuery<
    SalienceArticle[] | SalienceObservation[]
  >({
    queryKey: ["salience", "day-drill", agenda, termId, day],
    queryFn: () =>
      isMedia
        ? apiSalience.articlesLookup(termId, day)
        : apiSalience.observations({
            term: termId,
            from: day,
            to: day,
            ...(isElite ? { sources: ELITE_SOURCES } : {}),
          }),
    staleTime: Infinity,
    retry: 1,
  });

  const dayLabel = new Date(day).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const heading = isMedia ? "Coverage" : isElite ? "Activity" : "Videos";

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-terminal-panel p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
          {heading} · {dayLabel}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-1.5 text-[12px] text-terminal-ink-muted transition-colors hover:text-terminal-ink"
          aria-label="Close drilldown panel"
        >
          ✕
        </button>
      </div>
      {isLoading ? (
        isMedia ? (
          <>
            <ArticleListSkeleton rows={4} />
            <div className="mt-2 text-[10px] text-terminal-ink-muted/80">
              First look at this day — querying the GDELT archive…
            </div>
          </>
        ) : (
          <YouTubeVideoListSkeleton rows={4} />
        )
      ) : error ? (
        <div className="text-[12px] text-terminal-ink-muted">
          {isMedia ? "Coverage" : isElite ? "Activity" : "Video"} lookup failed
          — try again in a moment.
        </div>
      ) : (data ?? []).length === 0 ? (
        <div className="text-[12px] text-terminal-ink-muted">
          {isMedia
            ? `No archived articles match ${termLabel} on this day.`
            : isElite
              ? `No relevant parliamentary or government activity for ${termLabel} on this day.`
              : `No relevant videos published for ${termLabel} on this day.`}
        </div>
      ) : isMedia ? (
        <ArticleList articles={data as SalienceArticle[]} />
      ) : isElite ? (
        <EliteItemList items={data as SalienceObservation[]} />
      ) : (
        <YouTubeVideoList videos={data as SalienceObservation[]} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

interface TooltipProps {
  active?: boolean;
  label?: number;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  labelFor: (key: string) => string;
  fmtVal: (key: string, value: number) => string;
  uploads: { ts: number; title: string }[];
  events: { ts: number; label: string; eventType: string }[];
}

function TrendTooltip({
  active,
  label,
  payload,
  labelFor,
  fmtVal,
  uploads,
  events,
}: TooltipProps) {
  if (!active || label == null) return null;
  const dayUploads = uploads.filter((u) => u.ts === label);
  const dayEvents = events.filter((e) => e.ts === label);
  return (
    <div className="rounded-md border border-white/15 bg-terminal-elevated px-3 py-2 text-xs text-terminal-ink shadow-lg">
      <div className="mb-1 font-semibold">
        {shortDate(new Date(label).toISOString())}
      </div>
      {payload?.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 tabular-nums">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: p.color }}
          />
          <span className="text-terminal-ink-muted">{labelFor(p.dataKey)}</span>
          <span className="ml-auto font-semibold">
            {fmtVal(p.dataKey, p.value)}
          </span>
        </div>
      ))}
      {dayEvents.map((e, i) => (
        <div
          key={`e-${i}`}
          className="mt-1 border-t border-white/10 pt-1"
          style={{ color: EVENT_TYPE_COLOURS[e.eventType] }}
        >
          ◆ {e.label}
        </div>
      ))}
      {dayUploads.length > 0 && (
        <div className="mt-1 border-t border-white/10 pt-1 text-[#9aa0d8]">
          {dayUploads.length === 1
            ? `▶ ${truncate(dayUploads[0].title, 40)}`
            : `▶ ${dayUploads.length} Gary uploads`}
        </div>
      )}
    </div>
  );
}

function Legend({
  seriesKeys,
  colourFor,
  labelFor,
  isVisible,
  onToggle,
  events,
  showUploads,
  onToggleUploads,
  smooth,
  onToggleSmooth,
  hasSpikes,
}: {
  seriesKeys: string[];
  colourFor: (key: string) => string;
  labelFor: (key: string) => string;
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
  events: { eventType: string }[];
  showUploads: boolean;
  onToggleUploads: () => void;
  smooth: boolean;
  onToggleSmooth: () => void;
  hasSpikes: boolean;
}) {
  const eventTypes = [...new Set(events.map((e) => e.eventType))];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-terminal-ink-muted">
      {seriesKeys.map((k) => {
        const on = isVisible(k);
        const name = labelFor(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            aria-pressed={on}
            title={on ? `Hide ${name}` : `Show ${name}`}
            className={`flex items-center gap-1.5 rounded px-1 transition-colors hover:text-terminal-ink ${
              on ? "text-terminal-ink-muted" : "text-terminal-ink-muted/40"
            }`}
          >
            <span
              className="inline-block h-0.5 w-4 rounded"
              style={{ background: colourFor(k), opacity: on ? 1 : 0.3 }}
            />
            <span className={on ? "" : "line-through"}>{name}</span>
          </button>
        );
      })}
      {hasSpikes && (
        <>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-up" />
            Spike (near Gary upload)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-down" />
            Spike (other)
          </span>
        </>
      )}
      {eventTypes.map((t) => (
        <span key={t} className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-0 border-l border-dashed"
            style={{ borderColor: EVENT_TYPE_COLOURS[t] }}
          />
          {t.replace("_", " ")}
        </span>
      ))}
      <SmoothToggle
        smooth={smooth}
        onToggle={onToggleSmooth}
        windowDays={SMOOTH_WINDOW_DAYS}
        className="ml-auto"
      />
      <button
        type="button"
        onClick={onToggleUploads}
        aria-pressed={showUploads}
        className={`flex items-center gap-1.5 rounded px-2 py-0.5 transition-colors ${
          showUploads
            ? "bg-terminal-elevated text-terminal-ink"
            : "text-terminal-ink-muted hover:text-terminal-ink"
        }`}
      >
        <span className="text-[10px] text-[#aab2ff]">▲</span>
        Gary uploads
      </button>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
